// React Imports
import type { ReactNode } from 'react'

// Next Imports
// MUI Imports
import type { ChipProps } from '@mui/material/Chip'

// Type Imports
import type { Locale } from '@configs/i18n'
import type {
  VerticalMenuDataType,
  VerticalSectionDataType,
  VerticalSubMenuDataType,
  VerticalMenuItemDataType,
  HorizontalMenuDataType,
  HorizontalSubMenuDataType,
  HorizontalMenuItemDataType
} from '@/types/menuTypes'

import type { UserRole } from '@/types/rbac'

// Component Imports
import { SubMenu as HorizontalSubMenu, MenuItem as HorizontalMenuItem } from '@menu/horizontal-menu'
import { SubMenu as VerticalSubMenu, MenuItem as VerticalMenuItem, MenuSection } from '@menu/vertical-menu'
import CustomChip from '@core/components/mui/Chip'

// Hook Imports
import { useRBAC } from '@/hooks/useRBAC'

// Util Imports
import { getLocalizedUrl } from '@/utils/i18n'

// Generate a menu from the menu data array
export const GenerateVerticalMenu = ({ menuData }: { menuData: VerticalMenuDataType[] }) => {
  // Hooks
  const locale = 'en'
  const { hasRole, hasActionPermission } = useRBAC()

  const isItemVisible = (item: any) => {
    // Check roles
    if (item.requiredRoles && item.requiredRoles.length > 0) {
      if (!item.requiredRoles.some((role: UserRole) => hasRole(role))) {
        return false
      }
    }

    // Check permissions
    if (item.requiredPermissions && item.requiredPermissions.length > 0) {
      if (!item.requiredPermissions.some((perm: string) => hasActionPermission(perm))) {
        return false
      }
    }

    return true
  }

  const renderMenuItems = (data: VerticalMenuDataType[]) => {
    return data
      .filter(isItemVisible)
      .map((item: VerticalMenuDataType, index) => {
        const menuSectionItem = item as VerticalSectionDataType
        const subMenuItem = item as VerticalSubMenuDataType
        const menuItem = item as VerticalMenuItemDataType

        if (menuSectionItem.isSection) {
          const { children, isSection, requiredRoles, requiredPermissions, ...rest } = menuSectionItem
          const filteredChildren = children ? children.filter(isItemVisible) : []

          if (!filteredChildren || filteredChildren.length === 0) {
            return null
          }

          return (
            <MenuSection key={index} {...rest}>
              {renderMenuItems(filteredChildren)}
            </MenuSection>
          )
        }

        if (subMenuItem.children) {
          const { children, icon, prefix, suffix, requiredRoles, requiredPermissions, ...rest } = subMenuItem
          const filteredChildren = children ? children.filter(isItemVisible) : []

          if (!filteredChildren || filteredChildren.length === 0) {
            return null
          }

          // A dropdown holding a single visible link is pointless — render that
          // link directly, keeping the group's icon instead of the bullet icon
          const onlyChild = filteredChildren.length === 1 ? (filteredChildren[0] as VerticalSubMenuDataType) : null

          if (onlyChild && !onlyChild.children) {
            return renderMenuItem({ ...(onlyChild as VerticalMenuItemDataType), icon: icon ?? onlyChild.icon }, index)
          }

          const Icon = icon ? <i className={icon} /> : null

          const subMenuPrefix: ReactNode = prefix && (prefix as ChipProps).label ? (
            <CustomChip size='small' round='true' {...(prefix as ChipProps)} />
          ) : (prefix as ReactNode)

          const subMenuSuffix: ReactNode = suffix && (suffix as ChipProps).label ? (
            <CustomChip size='small' round='true' {...(suffix as ChipProps)} />
          ) : (suffix as ReactNode)

          return (
            <VerticalSubMenu
              key={index}
              prefix={subMenuPrefix}
              suffix={subMenuSuffix}
              {...rest}
              {...(Icon && { icon: Icon })}
            >
              {renderMenuItems(filteredChildren)}
            </VerticalSubMenu>
          )
        }

        return renderMenuItem(menuItem, index)
      })
      .filter(Boolean)
  }

  const renderMenuItem = (menuItem: VerticalMenuItemDataType, index: number) => {
    const { label, excludeLang, icon, prefix, suffix, requiredRoles, requiredPermissions, ...rest } = menuItem

    const href = rest.href?.startsWith('http')
      ? rest.href
      : rest.href && (excludeLang ? rest.href : getLocalizedUrl(rest.href, locale as Locale))

    const Icon = icon ? <i className={icon} /> : null

    const menuItemPrefix: ReactNode = prefix && (prefix as ChipProps).label ? (
      <CustomChip size='small' round='true' {...(prefix as ChipProps)} />
    ) : (prefix as ReactNode)

    const menuItemSuffix: ReactNode = suffix && (suffix as ChipProps).label ? (
      <CustomChip size='small' round='true' {...(suffix as ChipProps)} />
    ) : (suffix as ReactNode)

    return (
      <VerticalMenuItem
        key={index}
        prefix={menuItemPrefix}
        suffix={menuItemSuffix}
        {...rest}
        href={href}
        {...(Icon && { icon: Icon })}
      >
        {label}
      </VerticalMenuItem>
    )
  }

  return <>{renderMenuItems(menuData)}</>
}

export const GenerateHorizontalMenu = ({ menuData }: { menuData: HorizontalMenuDataType[] }) => {
  const locale = 'en'
  const { hasRole, hasActionPermission } = useRBAC()

  const isItemVisible = (item: any) => {
    if (item.requiredRoles && item.requiredRoles.length > 0) {
      if (!item.requiredRoles.some((role: UserRole) => hasRole(role))) {
        return false
      }
    }

    if (item.requiredPermissions && item.requiredPermissions.length > 0) {
      if (!item.requiredPermissions.some((perm: string) => hasActionPermission(perm))) {
        return false
      }
    }

    return true
  }

  const renderMenuItems = (data: HorizontalMenuDataType[]) => {
    return data
      .filter(isItemVisible)
      .map((item: HorizontalMenuDataType, index) => {
        const subMenuItem = item as HorizontalSubMenuDataType
        const menuItem = item as HorizontalMenuItemDataType

        if (subMenuItem.children) {
          const { children, icon, prefix, suffix, requiredRoles, requiredPermissions, ...rest } = subMenuItem
          const filteredChildren = children ? children.filter(isItemVisible) : []

          if (!filteredChildren || filteredChildren.length === 0) {
            return null
          }

          const Icon = icon ? <i className={icon} /> : null

          const subMenuPrefix: ReactNode = prefix && (prefix as ChipProps).label ? (
            <CustomChip size='small' round='true' {...(prefix as ChipProps)} />
          ) : (prefix as ReactNode)

          const subMenuSuffix: ReactNode = suffix && (suffix as ChipProps).label ? (
            <CustomChip size='small' round='true' {...(suffix as ChipProps)} />
          ) : (suffix as ReactNode)

          return (
            <HorizontalSubMenu
              key={index}
              prefix={subMenuPrefix}
              suffix={subMenuSuffix}
              {...rest}
              {...(Icon && { icon: Icon })}
            >
              {renderMenuItems(filteredChildren)}
            </HorizontalSubMenu>
          )
        }

        const { label, excludeLang, icon, prefix, suffix, requiredRoles, requiredPermissions, ...rest } = menuItem

        const href = rest.href?.startsWith('http')
          ? rest.href
          : rest.href && (excludeLang ? rest.href : getLocalizedUrl(rest.href, locale as Locale))

        const Icon = icon ? <i className={icon} /> : null

        const menuItemPrefix: ReactNode = prefix && (prefix as ChipProps).label ? (
          <CustomChip size='small' round='true' {...(prefix as ChipProps)} />
        ) : (prefix as ReactNode)

        const menuItemSuffix: ReactNode = suffix && (suffix as ChipProps).label ? (
          <CustomChip size='small' round='true' {...(suffix as ChipProps)} />
        ) : (suffix as ReactNode)

        return (
          <HorizontalMenuItem
            key={index}
            prefix={menuItemPrefix}
            suffix={menuItemSuffix}
            {...rest}
            href={href}
            {...(Icon && { icon: Icon })}
          >
            {label}
          </HorizontalMenuItem>
        )
      })
      .filter(Boolean)
  }

  return <>{renderMenuItems(menuData)}</>
}
